import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Search, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const usd = (v) => v == null ? "—" : `$${Number(v).toFixed(2)}`;

export default function AdminAccounts() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [broker, setBroker] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: 25, status });
      if (search) p.set("search", search);
      if (broker) p.set("broker", broker);
      const r = await api.get(`/admin/accounts?${p.toString()}`);
      setRows(r.data.accounts);
      setTotal(r.data.total);
    } catch (e) { toast.error("Failed to load accounts"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status]);

  return (
    <div className="space-y-4" data-testid="admin-accounts-page">
      <header>
        <h1 className="font-heading font-black text-3xl tracking-tighter">Accounts</h1>
        <div className="vx-label mt-1">{total} total across all users</div>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6b7280]" />
          <Input placeholder="Search account name…" value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
                 className="pl-8 bg-[#0a0e1a] border-[#1f2937] font-mono text-xs h-8"
                 data-testid="accounts-search-input" />
        </div>
        <Input placeholder="Broker (e.g. vantage)" value={broker}
               onChange={(e) => setBroker(e.target.value)}
               onKeyDown={(e) => e.key === "Enter" && (setPage(1), load())}
               className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs h-8 w-40"
               data-testid="accounts-broker-input" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                className="bg-[#0a0e1a] border border-[#1f2937] text-xs font-mono rounded h-8 px-2"
                data-testid="accounts-status-select">
          <option value="all">All EA</option>
          <option value="online">EA Online</option>
          <option value="offline">EA Offline</option>
        </select>
        <Button size="sm" variant="outline" onClick={() => { setPage(1); load(); }}
                className="border-[#1f2937] hover:bg-[#1f2937] text-xs h-8" data-testid="accounts-search-btn">
          Apply
        </Button>
      </div>

      <div className="rounded-lg border border-[#1f2937] bg-[#111827] overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead className="bg-[#0a0e1a] text-[#6b7280]">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Account</th>
              <th className="text-left px-3 py-2">Broker</th>
              <th className="text-right px-3 py-2">Balance</th>
              <th className="text-right px-3 py-2">Equity</th>
              <th className="text-center px-3 py-2">EA</th>
              <th className="text-center px-3 py-2">Auto-Exec</th>
              <th className="text-left px-3 py-2">Strategy</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="accounts-table-body">
            {loading ? (
              <tr><td colSpan={9} className="p-6 text-center text-[#6b7280]">
                <Loader2 className="w-4 h-4 animate-spin inline" /> Loading…
              </td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-6 text-center text-[#6b7280]">No accounts match your filter.</td></tr>
            ) : rows.map((a) => (
              <tr key={a.id} className="border-t border-[#1f2937] hover:bg-[#1f2937]/40"
                  data-testid={`account-row-${a.id}`}>
                <td className="px-3 py-2 text-[#e5e7eb]">{a.user_email || "—"}</td>
                <td className="px-3 py-2 text-[#9ca3af]">{a.account_name}</td>
                <td className="px-3 py-2 uppercase text-[#3b82f6]">{a.broker_id}</td>
                <td className="px-3 py-2 text-right">{usd(a.balance)}</td>
                <td className="px-3 py-2 text-right">{usd(a.equity)}</td>
                <td className="px-3 py-2 text-center">
                  {a.is_online
                    ? <span className="text-[9px] font-mono text-[#10b981] px-1.5 py-0.5 rounded border border-[#10b981]/40">● online</span>
                    : <span className="text-[9px] font-mono text-[#6b7280] px-1.5 py-0.5 rounded border border-[#6b7280]/40">● offline</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {a.auto_execute_enabled ? "✓" : "—"}
                </td>
                <td className="px-3 py-2 text-[#a78bfa]">{a.active_strategy_profile_id}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => navigate(`/admin/users/${a.user_id}`)}
                          className="p-1 rounded hover:bg-[#1f2937] text-[#3b82f6]"
                          data-testid={`view-user-${a.id}`}>
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center text-xs font-mono text-[#9ca3af]">
        <div>Page {page} of {Math.max(1, Math.ceil(total / 25))}</div>
        <div className="space-x-1">
          <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                  className="border-[#1f2937] hover:bg-[#1f2937] h-7 text-xs" data-testid="accounts-page-prev">Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={page * 25 >= total}
                  className="border-[#1f2937] hover:bg-[#1f2937] h-7 text-xs" data-testid="accounts-page-next">Next</Button>
        </div>
      </div>
    </div>
  );
}
